<template>
  <Popover v-slot="{ close }" class="relative">
    <PopoverButton
      class="flex w-full cursor-pointer items-center gap-4 px-3 py-2 text-yapgray-800 dark:border-yapdarkgray-600 dark:bg-yapdarkgray-800 dark:text-yapgray-100"
      :class="{
        'rounded-lg border shadow-sm': !grouped,
        'hover:text-primary-500 dark:hover:text-primary-dark-500': !readonly,
        'bg-yapgray-100 text-yapgray-600 dark:bg-yapdarkgray-900 dark:text-yapgray-200': readonly
      }"
      v-bind="$attrs"
      :disabled="readonly"
      ref="reference"
      @click="handleOpen"
    >
      <YapIcon :name="`${currentCategory}-outline`" class="h-8 w-8" />
      <div class="flex-1 text-left">
        <div class="text-xs text-yapgray-500">{{ $t('dict.category') }}</div>
        <div class="">
          {{ currentCategoryLocalized }}
        </div>
      </div>
      <YapIcon name="source-icons-list" class="ml-auto mr-0 w-6" />
    </PopoverButton>
    <PopoverPanel
      class="z-[100] w-80 overflow-hidden rounded-lg border shadow-lg drop-shadow-md dark:border-yapdarkgray-700"
      ref="floating"
      :style="floatingStyles"
    >
      <div class="relative w-full bg-white dark:bg-yapdarkgray-900">
        <div class="w-full bg-white p-3 shadow dark:bg-yapdarkgray-950">
          <YapInput
            id="category-search"
            name="category-search"
            :label="$t('dict.search')"
            :schema="{ name: 'search', type: 'string', maxLength: 140 }"
            :floating="true"
            v-model="state.categoryFilter"
            right-icon="search-outline"
          />
        </div>

        <ul v-if="state.trxCustomizeSchema" class="h-96 overflow-y-auto px-2" ref="categoryList">
          <li
            v-for="(category, key) in filteredCategories"
            :key="category"
            class="flex cursor-pointer items-center gap-2 p-3 hover:text-primary-500 dark:text-primary-dark-100 dark:hover:text-primary-dark-300"
            :class="
              currentCategory === key ? 'yap-selected-category rounded-lg bg-primary-50 dark:bg-primary-dark-900' : ''
            "
            @click="changeCategory(`${key}`, close)"
          >
            <YapIcon :name="`${key}-outline`" class="w-8" />
            {{ category }}
            <YapIcon
              name="checkmark-outline"
              class="ml-auto mr-0 w-6 text-primary-500 dark:text-primary-dark-200"
              v-if="currentCategory === key"
            />
          </li>
        </ul>
      </div>
    </PopoverPanel>
  </Popover>
</template>

<script setup lang="ts">
import { until } from '@vueuse/core';
import { computed, onMounted, reactive, ref } from 'vue';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { useFloating, autoUpdate, autoPlacement, offset, Placement, Strategy } from '@floating-ui/vue';

import { Schema } from '@/types/general';
import YapIcon from '@/components/YapIcon.vue';
import { useSchemasStore } from '@/store/schemaStore';
import YapInput from '@/components/form/YapInput.vue';

const props = defineProps<{
  currentCategory: string;
  grouped?: boolean;
  placement?: Placement;
  strategy?: Strategy;
  fallbackPlacements?: Array<Placement>;
  readonly?: boolean;
}>();

const emit = defineEmits(['update:modelValue']);

const reference = ref(null);
const floating = ref(null);

const { floatingStyles } = useFloating(reference, floating, {
  placement: props.placement ?? 'bottom',
  strategy: props.strategy,
  whileElementsMounted: autoUpdate,
  middleware: [autoPlacement(), offset(5)]
});

interface State {
  categoryFilter?: string;
  trxCustomizeSchema?: Schema;
}

const state: State = reactive({
  categoryFilter: undefined,
  trxCustomizeSchema: undefined
});

const schemaStore = useSchemasStore();
onMounted(async () => {
  state.trxCustomizeSchema = await schemaStore.getSchema({ path: '/v2/transactions/1/customize' });
});

interface Categories {
  [key: string]: string;
}

const categories = computed(() => {
  const keys = state.trxCustomizeSchema?.properties.category.enum;
  const values = state.trxCustomizeSchema?.properties.category['x-enumLabels'];
  const categories: Categories = {};
  keys?.forEach((item: string, i: number) => {
    categories[item] = values[i];
  });
  return categories;
});

const filteredCategories = computed(() => {
  if (!state.categoryFilter) {
    return categories.value;
  }

  const filtered = Object.entries(categories.value).filter(([key, value]) => {
    return value.toLowerCase().includes(state.categoryFilter?.toLowerCase() ?? '');
  });

  return Object.fromEntries(filtered);
});

const currentCategoryLocalized = computed(() =>
  props.currentCategory ? categories.value[props.currentCategory] : 'n/a'
);

async function changeCategory(category: string, close: any) {
  emit('update:modelValue', category);
  close();
}

const categoryList = ref<HTMLElement | null>(null);

async function handleOpen() {
  await until(() => !!categoryList.value).toBe(true);

  const selected = categoryList.value?.querySelector('.yap-selected-category');
  if (selected) {
    const dropdown = categoryList.value;
    if (dropdown) {
      const dropdownRect = dropdown.getBoundingClientRect();
      const selectedRect = selected.getBoundingClientRect();

      const relativeBottom = selectedRect.bottom - dropdownRect.bottom;
      const selectedHeight = selectedRect.height;

      if (relativeBottom > 0) {
        // If the bottom of the selected element is out of view, scroll so it comes fully into view
        dropdown.scrollTop += relativeBottom + selectedHeight;
      }
    }
  }
}
</script>
